import { KeenIcon } from '@/components';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import ViewYatchClub from './ViewYatchClub';
import { MembersListing } from '../club-members/MembersListing';
import { HarborAssociationListing } from './HarborAssociationListing';
import { ClubResourcesListing, MemberShiftsListing } from '../club-resource';
import ClubAvailabilityCalendar from './ClubAvailabilityCalendar';
import InspectorSlotCalendar from './InspectorSlotCalendar';
import { ClubBookingListings } from '../club-booking-management';
import { usePermissions } from '@/hooks/usePermissions';
import { ClubBoatManagementTab } from './ClubBoatManagementTab';
import { MembersRosterListing } from '../members-roster';
import { useSelector } from 'react-redux';
import { selectClubCapabilities } from '@/store/clubCapabilitiesSlice';
import { ClubCapability } from '@/constant/Club';

const YatchClubDetailsView = () => {
  const { id: clubId } = useParams<{ id: string }>();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();

  // Get club capabilities from Redux
  const clubCapabilities = useSelector(selectClubCapabilities);

  // Check if DISCOVERY_HARBOR_LIST_BASIC or OPERATIONS_HARBOR_BERTH_STACK capability is present
  const hasHarborListCapability = useMemo(() => {
    if (!clubCapabilities || clubCapabilities.length === 0) {
      return false;
    }
    return (
      clubCapabilities.includes(ClubCapability.DISCOVERY_HARBOR_LIST_BASIC) ||
      clubCapabilities.includes(ClubCapability.OPERATIONS_HARBOR_BERTH_STACK)
    );
  }, [clubCapabilities]);

  // Check if OPERATIONS_INTERNAL_RESOURCES_BOOKING capability is present
  const hasInternalResourcesBookingCapability = useMemo(() => {
    if (!clubCapabilities || clubCapabilities.length === 0) {
      return false;
    }
    return clubCapabilities.includes(
      ClubCapability.OPERATIONS_INTERNAL_RESOURCES_BOOKING
    );
  }, [clubCapabilities]);

  // Check if OPERATIONS_FEDERATION_MEMBER_MANAGEMENT capability is present
  const hasFederationMemberManagementCapability = useMemo(() => {
    if (!clubCapabilities || clubCapabilities.length === 0) {
      return false;
    }
    return clubCapabilities.includes(
      ClubCapability.OPERATIONS_FEDERATION_MEMBER_MANAGEMENT
    );
  }, [clubCapabilities]);

  const urlSearchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const tabFromUrl = urlSearchParams.get('tab');

  // Define all available tabs
  const navItems = useMemo(() => {
    const items = [
      { name: 'Club Management', href: '#', id: 'club-management' },
      { name: 'Availability Calendar', href: '#', id: 'availability-calendar' },
      { name: 'Inspector Calendar', href: '#', id: 'inspector-calendar' },
      { name: 'Club Requests', href: '#', id: 'club-requests' },
      { name: 'Boat Management', href: '#', id: 'boat-management' },
    ];

    // Add Club Members, Members Roster, and Member Shifts tabs only if OPERATIONS_FEDERATION_MEMBER_MANAGEMENT capability is present
    if (hasFederationMemberManagementCapability) {
      items.splice(
        3,
        0,
        { name: 'Club Members', href: '#', id: 'club-members' },
        { name: 'Members Roster', href: '#', id: 'members-roster' },
        { name: 'Member Shifts', href: '#', id: 'member-shifts' }
      );
    }

    // Add Club Resources tab only if OPERATIONS_INTERNAL_RESOURCES_BOOKING capability is present
    if (hasInternalResourcesBookingCapability) {
      items.push({
        name: 'Club Resources',
        href: '#',
        id: 'club-resources',
      });
    }

    // Add Resource Bookings tab only if OPERATIONS_INTERNAL_RESOURCES_BOOKING capability is present
    if (hasInternalResourcesBookingCapability) {
      items.push({
        name: 'Resource Bookings',
        href: '#',
        id: 'resource-bookings',
      });
    }

    // Add Associated Harbor tab only if DISCOVERY_HARBOR_LIST_BASIC or OPERATIONS_HARBOR_BERTH_STACK capability is present
    if (hasHarborListCapability) {
      items.push({
        name: 'Associated Harbor',
        href: '#',
        id: 'associated-harbor',
      });
    }

    return items;
  }, [
    hasHarborListCapability,
    hasInternalResourcesBookingCapability,
    hasFederationMemberManagementCapability,
  ]);

  // Permission checks per tab (reuse existing read permission keys used across management pages)
  const isTabAllowed = useCallback(
    (tabId: string) => {
      switch (tabId) {
        case 'club-management':
          return hasPermission('club_management_read');
        case 'availability-calendar':
          return hasPermission('club_resource_availability_calender');
        case 'inspector-calendar':
          return hasPermission('club_resource_booking_management_read');
        case 'club-members':
          return hasPermission('read_club_members');
        case 'members-roster':
          return hasPermission('club_member_roster_management_read');
        // case "club-requests":
        //   return hasPermission("read_membership_requests");
        case 'club-resources':
          return hasPermission('club_resource_management_read');
        case 'resource-bookings':
          return hasPermission('club_resource_booking_management_read');
        case 'member-shifts':
          return hasPermission('club_resource_booking_management_read');
        case 'boat-management':
          return hasPermission('club_boat_management_read');
        case 'associated-harbor':
          // No explicit harbor-association read key found; align with harbor read access
          return hasPermission('harbor_management_read');
        default:
          return false;
      }
    },
    [hasPermission]
  );

  const allowedNavItems = useMemo(
    () => navItems.filter((item) => isTabAllowed(item.id)),
    [navItems, isTabAllowed]
  );

  const [activeTab, setActiveTab] = useState(
    tabFromUrl && navItems.some((item) => item.id === tabFromUrl)
      ? tabFromUrl
      : ''
  );

  // Backward compatibility: if the URL uses hash for tabs, convert it to ?tab=...
  useEffect(() => {
    const hashTab = location.hash.replace('#', '');
    if (!hashTab) return;

    const currentParams = new URLSearchParams(location.search);
    if (!currentParams.get('tab')) {
      currentParams.set('tab', hashTab);
      navigate(
        {
          pathname: location.pathname,
          search: `?${currentParams.toString()}`,
        },
        { replace: true }
      );
    }
  }, [location.hash, location.pathname, location.search, navigate]);

  // Determine the back navigation path based on where user came from
  const getBackPath = () => {
    // Prefer explicit return path (e.g., coming from Harbor Details with ?tab=...)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const fromState = (location.state as any)?.from as string | undefined;
    if (fromState) return fromState;

    // Check if user came from club resources page via state
    if (location.state?.from === 'club-resources') {
      return '/club-management';
    }

    // Check if current active tab is club-resources, indicating user navigated from resources
    if (activeTab === 'club-resources') {
      return '/club-management';
    }

    // Check referrer or browser history for club-resource paths
    if (document.referrer && document.referrer.includes('/club-resource')) {
      return '/club-management';
    }

    // Default to club management
    return '/club-management';
  };

  // Set initial active tab based on navigation context
  useEffect(() => {
    if (allowedNavItems.length > 0 && !activeTab) {
      // Prefer explicit active tab from navigation state
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const activeTabFromState = (location.state as any)?.activeTab as
        | string
        | undefined;
      if (
        activeTabFromState &&
        allowedNavItems.some((item) => item.id === activeTabFromState)
      ) {
        setActiveTab(activeTabFromState);
        return;
      }

      if (
        tabFromUrl &&
        allowedNavItems.some((item) => item.id === tabFromUrl)
      ) {
        setActiveTab(tabFromUrl);
        return;
      }

      // Check if user came from club resources page
      if (
        location.state?.from === 'club-resources' ||
        document.referrer.includes('/club-resource') ||
        window.location.pathname.includes('club-resource')
      ) {
        // Check if club-resources tab is allowed
        if (allowedNavItems.some((item) => item.id === 'club-resources')) {
          setActiveTab('club-resources');
          return;
        }
      }
      // Default to first allowed tab
      setActiveTab(allowedNavItems[0].id);
    }
  }, [allowedNavItems, activeTab, location.state, tabFromUrl]);

  // Update URL when tab changes
  useEffect(() => {
    if (!activeTab) return;
    const currentParams = new URLSearchParams(location.search);
    if (currentParams.get('tab') !== activeTab) {
      currentParams.set('tab', activeTab);
      navigate(
        {
          pathname: location.pathname,
          search: `?${currentParams.toString()}`,
        },
        { replace: true }
      );
    }
  }, [activeTab, location.pathname, location.search, navigate]);

  // Ensure active tab stays valid when permissions change
  useEffect(() => {
    if (!allowedNavItems.some((item) => item.id === activeTab)) {
      if (allowedNavItems.length > 0) {
        setActiveTab(allowedNavItems[0].id);
      }
    }
  }, [allowedNavItems, activeTab]);

  const currentIndex = allowedNavItems.findIndex(
    (item) => item.id === activeTab
  );
  const goPrevTab = () => {
    const list = allowedNavItems;
    if (list.length === 0) return;
    const prevIndex = (currentIndex - 1 + list.length) % list.length;
    const nextTabId = list[prevIndex].id;
    setActiveTab(nextTabId);
  };
  const goNextTab = () => {
    const list = allowedNavItems;
    if (list.length === 0) return;
    const nextIndex = (currentIndex + 1) % list.length;
    const nextTabId = list[nextIndex].id;
    setActiveTab(nextTabId);
  };
  const renderContent = () => {
    // Prevent rendering unauthorized content
    if (!allowedNavItems.some((item) => item.id === activeTab)) {
      return null;
    }

    switch (activeTab) {
      case 'club-management':
        return <ViewYatchClub />;
      case 'availability-calendar':
        return <ClubAvailabilityCalendar />;
      case 'inspector-calendar':
        return <InspectorSlotCalendar />;
      case 'club-members':
        return <MembersListing />;
      case 'members-roster':
        return <MembersRosterListing />;
      // case "club-requests":
      //   return <ClubRequestsListing />;
      case 'club-resources':
        return <ClubResourcesListing clubId={clubId} />;
      case 'resource-bookings':
        return <ClubBookingListings clubId={clubId} />;
      case 'member-shifts':
        return <MemberShiftsListing clubId={clubId} />;
      case 'boat-management':
        return <ClubBoatManagementTab clubId={clubId} />;
      case 'associated-harbor':
        return <HarborAssociationListing clubId={clubId} />;
      default:
        return <ViewYatchClub />;
    }
  };
  return (
    <>
      <style>{`
        /* Responsive navigation styles */
        @media (min-width: 1024px) {
          .responsive-nav {
            justify-content: flex-start;
            overflow-x: auto; /* Allow horizontal scroll on desktop too */
          }
          .responsive-nav .nav-tab {
            flex: 0 0 auto;
            margin-right: 2rem;
          }
        }
        
        @media (max-width: 1023px) {
          .responsive-nav {
            overflow-x: auto;
            justify-content: flex-start;
          }
          .responsive-nav .nav-tab {
            flex-shrink: 0;
            margin-right: 1.5rem;
          }
        }
        
        /* Ensure last tab doesn't have margin */
        .responsive-nav .nav-tab:last-child {
          margin-right: 0;
        }
      `}</style>
      <div className="harbor-page w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 pt-4">
              <button
                onClick={() => navigate(getBackPath())}
                className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
                aria-label="Go back"
              >
                <KeenIcon icon="black-left" className="text-xl" />
              </button>
            </div>
            <header className="harbor-header md:border-b border-gray-200 flex-1 min-w-0 w-full">
              <nav role="tablist" className="-mb-px flex responsive-nav px-1" aria-label="Tabs">
                {allowedNavItems.map((item) => (
                  <Link
                    key={item.name}
                    id={`club-tab-${item.id}`}
                    to={`?tab=${item.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveTab(item.id);
                    }}
                    role="tab"
                    aria-selected={activeTab === item.id}
                    aria-controls={`club-tabpanel-${item.id}`}
                    className={`nav-tab whitespace-nowrap py-4 px-3 border-b-2 font-medium text-sm flex items-center gap-1 min-w-fit focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset rounded-sm ${
                      activeTab === item.id
                        ? 'border-blue-500 text-blue-600 contrast:text-blue-400'
                        : 'border-transparent text-gray-500 contrast:text-gray-900 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}
              </nav>
              {/* Mobile-only pager: hidden by default, shown at 320px via CSS */}
              <div
                className="harbor-mobile-nav hidden items-center justify-between"
                role="toolbar"
                aria-label="Tabs mobile"
              >
                <button
                  type="button"
                  className="nav-arrow prev"
                  onClick={goPrevTab}
                  aria-label="Previous section"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <div className="current-tab" aria-current="page">
                  {allowedNavItems[currentIndex]?.name}
                </div>
                <button
                  type="button"
                  className="nav-arrow next"
                  onClick={goNextTab}
                  aria-label="Next section"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </header>
          </div>
          <div
            className="py-8"
            role="tabpanel"
            id={`club-tabpanel-${activeTab}`}
            aria-label={`${allowedNavItems[currentIndex]?.name || 'Section'} content`}
          >
            {renderContent()}
          </div>
        </div>
      </div>
    </>
  );
};

export default YatchClubDetailsView;
